import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Divider,
  Input,
  LinearProgress,
  Option,
  Select,
  Sheet,
  Textarea,
  Typography,
} from '@mui/joy';
import type { ColorPaletteProp } from '@mui/joy';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AdminOnboardingAPI, AdminSequencesAPI } from '../../../lib/apiAdmin';
import {
  DetailDrawer,
  DetailMetric,
  DetailRow,
  DetailSection,
} from '../../../components/common/DetailDrawer';
import { StaffPicker } from '../../../components/admin/StaffPicker';
import type { HealthBand, StaffTask } from '../../../types/admin';
import { SETUP_STEP_LABELS, SETUP_TOTAL_STEPS } from '../../../types/admin';
import { TaskRow } from './TaskRow';
import { AccountSection } from '../accounts/AccountSection';
import { CXCustomerPlan } from './CXCustomerPlan';
import { useProfileDraft } from '../organizations/ProfileDrafts';
import { getApiErrorMessage } from '../../../lib/apiError';

const BAND_META: Record<HealthBand, { label: string; color: ColorPaletteProp }> = {
  healthy: { label: 'Healthy', color: 'success' },
  at_risk: { label: 'At risk', color: 'warning' },
  stalled: { label: 'Stalled', color: 'danger' },
};

const SIGNAL_LABELS: Record<string, string> = {
  logged_in_recently: 'Logged in recently',
  did_core_work: 'Created jobs or estimates',
  used_crm: 'Added contacts',
  completed_setup: 'Worked through setup',
  invited_team: 'Invited their team',
  no_open_tickets: 'No open support tickets',
};

const COUNT_LABELS: Record<string, string> = {
  contacts: 'Contacts',
  jobs: 'Jobs',
  estimates: 'Estimates',
  opportunities: 'Opportunities',
  users: 'Users',
  photos: 'Photos',
};

/**
 * One customer's onboarding: how they are doing, what they have not done, and
 * the tasks open against them.
 */
export const CustomerDetailDrawer: React.FC<{
  customerId: string | null;
  onClose: () => void;
  embedded?: boolean;
}> = ({ customerId, onClose, embedded = false }) => {
  const queryClient = useQueryClient();
  const [taskTitle, setTaskTitle] = useState('');
  const [notes, setNotes] = useState<string | null>(null);
  const [taskState, setTaskState] = useState<'open' | 'all'>('open');
  const [enrolInto, setEnrolInto] = useState<string | null>(null);

  const open = customerId !== null;

  const customerQuery = useQuery({
    queryKey: ['admin', 'customer', customerId],
    queryFn: () => AdminOnboardingAPI.getCustomer(customerId as string),
    enabled: open,
  });

  const customer = customerQuery.data;
  useProfileDraft('customer-notes', (notes !== null && notes !== (customer?.notes || '')) || !!taskTitle);

  const tasksQuery = useQuery({
    queryKey: ['admin', 'customer', customerId, 'tasks', taskState],
    queryFn: () => AdminOnboardingAPI.listTasks({ organization: customer?.organization, state: taskState }),
    enabled: open && Boolean(customer?.organization),
  });

  // The customer's own drip enrollments, plus the sequences they could be put
  // on. Enrolling by hand is the escape hatch for a customer who signed up
  // before a sequence existed.
  const enrollmentsQuery = useQuery({
    queryKey: ['admin', 'enrollments', { customer: customerId }],
    queryFn: () => AdminSequencesAPI.listEnrollments({ customer: customerId as string }),
    enabled: open,
  });

  const sequencesQuery = useQuery({
    queryKey: ['admin', 'sequences'],
    queryFn: () => AdminSequencesAPI.listSequences(),
    enabled: open,
  });

  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: ['admin', 'customer', customerId] });
    queryClient.invalidateQueries({ queryKey: ['admin', 'customers'] });
    queryClient.invalidateQueries({ queryKey: ['admin', 'org-profile'] });
    queryClient.invalidateQueries({ queryKey: ['admin-orgs'] });
    queryClient.invalidateQueries({ queryKey: ['admin-org'] });
  };

  const snoozeMutation = useMutation({
    mutationFn: () => AdminOnboardingAPI.snoozeCustomer(customerId!),
    onSuccess: refresh,
  });

  const snapshotMutation = useMutation({
    mutationFn: () => AdminOnboardingAPI.snapshotCustomer(customerId as string),
    onSuccess: refresh,
  });

  const updateMutation = useMutation({
    mutationFn: (changes: Record<string, unknown>) =>
      AdminOnboardingAPI.updateCustomer(customerId as string, changes),
    onSuccess: () => {
      setNotes(null);
      refresh();
    },
  });

  const invalidateTasks = () =>
    queryClient.invalidateQueries({ queryKey: ['admin', 'customer', customerId, 'tasks'] });

  const invalidateEnrollments = () => queryClient.invalidateQueries({ queryKey: ['admin', 'enrollments'] });

  const updateTaskMutation = useMutation({
    mutationFn: ({ id, changes }: { id: string; changes: Partial<StaffTask> }) =>
      AdminOnboardingAPI.updateTask(id, changes),
    onSuccess: invalidateTasks,
  });

  const reopenMutation = useMutation({
    mutationFn: (id: string) => AdminOnboardingAPI.reopenTask(id),
    onSuccess: () => {
      invalidateTasks();
      refresh();
    },
  });

  const enrolMutation = useMutation({
    mutationFn: (sequenceId: string) => AdminSequencesAPI.enrol(sequenceId, customerId as string),
    onSuccess: () => {
      setEnrolInto(null);
      invalidateEnrollments();
    },
  });

  const cancelEnrolMutation = useMutation({
    mutationFn: (id: string) => AdminSequencesAPI.cancelEnrollment(id),
    onSuccess: invalidateEnrollments,
  });

  const taskMutation = useMutation({
    mutationFn: () =>
      AdminOnboardingAPI.createTask({ title: taskTitle, organization: customer?.organization }),
    onSuccess: () => {
      setTaskTitle('');
      queryClient.invalidateQueries({ queryKey: ['admin', 'customer', customerId, 'tasks'] });
      refresh();
    },
  });

  const completeMutation = useMutation({
    mutationFn: (id: string) => AdminOnboardingAPI.completeTask(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'customer', customerId, 'tasks'] });
      refresh();
    },
  });

  const health = customer?.latest_health;
  const band = health ? BAND_META[health.band] : null;
  const tasks = tasksQuery.data?.results ?? [];
  const enrollments = enrollmentsQuery.data?.results ?? [];
  const enrolledSequenceIds = new Set(enrollments.map((row) => row.sequence));
  const availableSequences = (sequencesQuery.data?.results ?? []).filter(
    (sequence) => !enrolledSequenceIds.has(sequence.id),
  );

  const content = (
    <>
      {customerQuery.isPending && <Typography role="status">Loading onboarding details…</Typography>}
      {customerQuery.isError && (
        <Alert color="danger" endDecorator={<Button onClick={() => customerQuery.refetch()}>Retry</Button>}>
          Could not load onboarding details.
        </Alert>
      )}
      {(snoozeMutation.error ||
        updateMutation.error ||
        enrolMutation.error ||
        cancelEnrolMutation.error ||
        snapshotMutation.error) && (
        <Alert color="danger">
          {getApiErrorMessage(
            snoozeMutation.error ||
              updateMutation.error ||
              enrolMutation.error ||
              cancelEnrolMutation.error ||
              snapshotMutation.error,
            'Could not save changes.',
          )}
        </Alert>
      )}

      {customer && (
        <>
          <CXCustomerPlan key={customer.id} customerId={customer.id} />
          <AccountSection
            title="Account health & preferences"
            description="Automatic activity signals, the account owner, and ongoing relationship notes. These are separate from the onboarding steps your team checks off."
            help="Health is calculated from product activity and setup signals. Measuring health refreshes that snapshot; it does not complete onboarding steps."
            action={
              embedded ? (
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  <Button
                    variant="outlined"
                    size="sm"
                    loading={snapshotMutation.isPending}
                    onClick={() => snapshotMutation.mutate()}
                  >
                    Measure account health
                  </Button>
                  <Button
                    variant="outlined"
                    size="sm"
                    loading={snoozeMutation.isPending}
                    onClick={() => snoozeMutation.mutate()}
                  >
                    Snooze reminders 14 days
                  </Button>
                </Box>
              ) : undefined
            }
          >
            <Box
              component="details"
              sx={{ '& > summary': { cursor: 'pointer', fontWeight: 'lg' }, '&[open] > summary': { mb: 2 } }}
            >
              <summary>View health, owner, notes &amp; email preferences</summary>
              <Box sx={{ display: 'grid', gap: 2 }}>
                {health && band ? (
                  <Sheet variant="outlined" sx={{ p: 2, borderRadius: 'md', display: 'grid', gap: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Chip size="sm" variant="soft" color={band.color}>
                        {band.label}
                      </Chip>
                      <Typography level="title-lg">{health.score}</Typography>
                      <Box sx={{ flex: 1 }} />
                      <Typography level="body-xs" sx={{ color: 'text.tertiary' }}>
                        Measured {new Date(health.taken_on).toLocaleDateString()}
                      </Typography>
                    </Box>
                    <LinearProgress determinate value={health.score} color={band.color} sx={{ height: 6 }} />
                  </Sheet>
                ) : (
                  <Typography level="body-sm" sx={{ color: 'text.tertiary' }}>
                    Health has not been measured yet.
                  </Typography>
                )}

                {health && (
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    {Object.entries(health.counts)
                      .filter(([key]) => COUNT_LABELS[key])
                      .map(([key, value]) => (
                        <DetailMetric key={key} label={COUNT_LABELS[key]} value={value} />
                      ))}
                  </Box>
                )}

                {health && (
                  <DetailSection title="Setup">
                    {(() => {
                      // `setup_missing` names the steps still outstanding, so a call
                      // can be about job boards and services rather than about
                      // "setup".
                      const done = Number(health.counts.setup_steps ?? 0);
                      const missing = Array.isArray(health.counts.setup_missing)
                        ? (health.counts.setup_missing as string[])
                        : [];
                      return (
                        <Box sx={{ display: 'grid', gap: 1 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography level="body-sm">
                              {done} of {SETUP_TOTAL_STEPS} steps
                            </Typography>
                            <Box sx={{ flex: 1 }}>
                              <LinearProgress
                                determinate
                                value={(done / SETUP_TOTAL_STEPS) * 100}
                                color={done >= SETUP_TOTAL_STEPS ? 'success' : 'neutral'}
                                sx={{ height: 5 }}
                              />
                            </Box>
                          </Box>
                          {missing.length > 0 && (
                            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                              {missing.map((step) => (
                                <Chip key={step} size="sm" variant="outlined" color="neutral">
                                  {SETUP_STEP_LABELS[step] ?? step.replace(/_/g, ' ')}
                                </Chip>
                              ))}
                            </Box>
                          )}
                        </Box>
                      );
                    })()}
                  </DetailSection>
                )}

                {health && (
                  <DetailSection title="Where they stand">
                    <Box sx={{ display: 'grid', gap: 0.5 }}>
                      {Object.entries(health.signals).map(([key, done]) => (
                        <Box key={key} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Chip size="sm" variant="soft" color={done ? 'success' : 'neutral'}>
                            {done ? 'Done' : 'Not yet'}
                          </Chip>
                          <Typography
                            level="body-sm"
                            sx={{ color: done ? 'text.primary' : 'text.secondary' }}
                          >
                            {SIGNAL_LABELS[key] ?? key}
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                  </DetailSection>
                )}

                <Divider />

                <DetailSection title="Customer Success owner">
                  <StaffPicker
                    value={customer.cs_owner}
                    onChange={(userId) => updateMutation.mutate({ cs_owner: userId })}
                    placeholder="Unassigned"
                    disabled={updateMutation.isPending}
                  />
                </DetailSection>

                <DetailSection
                  title="Notes"
                  action={
                    <Button
                      size="sm"
                      variant="plain"
                      color={customer.do_not_email ? 'danger' : 'neutral'}
                      onClick={() => updateMutation.mutate({ do_not_email: !customer.do_not_email })}
                    >
                      {customer.do_not_email ? 'Do not email' : 'Emails allowed'}
                    </Button>
                  }
                >
                  <Textarea
                    size="sm"
                    minRows={2}
                    placeholder="What should the next person know?"
                    value={notes ?? customer.notes}
                    onChange={(event) => setNotes(event.target.value)}
                  />
                  {notes !== null && notes !== customer.notes && (
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                      <Button
                        size="sm"
                        variant="outlined"
                        loading={updateMutation.isPending}
                        onClick={() => updateMutation.mutate({ notes })}
                      >
                        Save notes
                      </Button>
                    </Box>
                  )}
                </DetailSection>

                <Box>
                  <DetailRow
                    label="Last activity"
                    value={
                      health?.days_since_activity == null ? 'Never' : `${health.days_since_activity} days ago`
                    }
                  />
                  <DetailRow label="Open tickets" value={health?.open_ticket_count ?? 0} />
                  <DetailRow
                    label="Nudges sent"
                    value={Object.keys(customer.nudge_log ?? {}).length || '—'}
                  />
                  {customer.snoozed_until && (
                    <DetailRow
                      label="Snoozed until"
                      value={new Date(customer.snoozed_until).toLocaleDateString()}
                    />
                  )}
                </Box>

                <Divider />

                {!embedded && (
                  <DetailSection
                    title={`Tasks (${tasks.filter((t) => t.is_open).length} open)`}
                    action={
                      <Button
                        size="sm"
                        variant="plain"
                        color="neutral"
                        onClick={() => setTaskState((prev) => (prev === 'open' ? 'all' : 'open'))}
                      >
                        {taskState === 'open' ? 'Show completed' : 'Open only'}
                      </Button>
                    }
                  >
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Input
                        size="sm"
                        placeholder="Something to do"
                        value={taskTitle}
                        onChange={(event) => setTaskTitle(event.target.value)}
                        sx={{ flex: 1 }}
                      />
                      <Button
                        size="sm"
                        variant="outlined"
                        disabled={!taskTitle.trim()}
                        loading={taskMutation.isPending}
                        onClick={() => taskMutation.mutate()}
                      >
                        Add
                      </Button>
                    </Box>

                    <Box sx={{ display: 'grid', gap: 0.75, mt: 1 }}>
                      {tasks.map((task) => (
                        <TaskRow
                          key={task.id}
                          task={task}
                          isBusy={
                            (completeMutation.isPending && completeMutation.variables === task.id) ||
                            (reopenMutation.isPending && reopenMutation.variables === task.id)
                          }
                          onUpdate={(changes) => updateTaskMutation.mutate({ id: task.id, changes })}
                          onComplete={() => completeMutation.mutate(task.id)}
                          onReopen={() => reopenMutation.mutate(task.id)}
                        />
                      ))}
                      {tasks.length === 0 && (
                        <Typography level="body-sm" sx={{ color: 'text.tertiary' }}>
                          {taskState === 'open' ? 'Nothing open.' : 'No tasks yet.'}
                        </Typography>
                      )}
                    </Box>
                  </DetailSection>
                )}
                <Divider />
              </Box>
            </Box>
          </AccountSection>
          <AccountSection
            title="Email sequence enrollments"
            description="See which automated follow-ups this customer is receiving. Enrolling starts the sequence; Stop prevents its remaining emails."
          >
            <Box
              component="details"
              sx={{ '& > summary': { cursor: 'pointer', fontWeight: 'lg' }, '&[open] > summary': { mb: 2 } }}
            >
              <summary>View &amp; manage enrollments ({enrollments.length})</summary>
              <Box sx={{ display: 'grid', gap: 2 }}>
                <Box sx={{ display: 'grid', gap: 0.5 }}>
                  {enrollments.map((enrollment) => (
                    <Box key={enrollment.id} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Chip
                        size="sm"
                        variant="soft"
                        color={enrollment.status === 'active' ? 'primary' : 'neutral'}
                      >
                        {enrollment.status}
                      </Chip>
                      <Typography level="body-sm" sx={{ flex: 1, minWidth: 0 }} noWrap>
                        {enrollment.sequence_name}
                      </Typography>
                      <Typography level="body-xs" sx={{ color: 'text.tertiary' }}>
                        {enrollment.steps_run} sent
                      </Typography>
                      {enrollment.status === 'active' && (
                        <Button
                          size="sm"
                          variant="plain"
                          color="neutral"
                          loading={
                            cancelEnrolMutation.isPending && cancelEnrolMutation.variables === enrollment.id
                          }
                          onClick={() => cancelEnrolMutation.mutate(enrollment.id)}
                        >
                          Stop
                        </Button>
                      )}
                    </Box>
                  ))}
                  {enrollments.length === 0 && (
                    <Typography level="body-sm" sx={{ color: 'text.tertiary' }}>
                      Not on any sequence.
                    </Typography>
                  )}
                </Box>

                {availableSequences.length > 0 && (
                  <Box sx={{ display: 'flex', gap: 1, mt: 0.5 }}>
                    <Select
                      size="sm"
                      placeholder="Put them on a sequence"
                      value={enrolInto}
                      onChange={(_, value) => setEnrolInto((value as string) ?? null)}
                      sx={{ flex: 1 }}
                    >
                      {availableSequences.map((sequence) => (
                        <Option key={sequence.id} value={sequence.id}>
                          {sequence.name}
                          {sequence.is_active ? '' : ' (paused)'}
                        </Option>
                      ))}
                    </Select>
                    <Button
                      size="sm"
                      variant="outlined"
                      disabled={!enrolInto}
                      loading={enrolMutation.isPending}
                      onClick={() => enrolInto && enrolMutation.mutate(enrolInto)}
                    >
                      Enroll
                    </Button>
                  </Box>
                )}
              </Box>
            </Box>
          </AccountSection>
        </>
      )}
    </>
  );
  if (embedded) return <Box sx={{ display: 'grid', gap: 3, pt: 2 }}>{content}</Box>;
  return (
    <DetailDrawer
      open={open}
      onClose={onClose}
      title={customer?.organization_name ?? 'Customer'}
      subtitle={
        customer ? `Signed up ${new Date(customer.organization_created_at).toLocaleDateString()}` : undefined
      }
      isLoading={customerQuery.isLoading}
      error={customerQuery.error as Error | null}
      actions={
        <Button
          size="sm"
          variant="outlined"
          color="neutral"
          loading={snapshotMutation.isPending}
          onClick={() => snapshotMutation.mutate()}
        >
          Measure now
        </Button>
      }
    >
      {content}
    </DetailDrawer>
  );
};

export default CustomerDetailDrawer;
